import { makeStyles } from '@material-ui/styles';
import { Colors, flattenMessages, getTypedLanguage } from '@rentguru/commons-utils';
import { Grid, LoadingProgress, Typography } from '@up2rent/ui';
import { useEffect } from 'react';
import { IntlProvider, useIntl } from 'react-intl';

import { initLanguage } from 'src/hooks/UserContext';
import { providerLocaleMap } from 'src/i18n/IntlProviderWrapper';
import { getCommunicationAttachmentsDownloadUrl } from 'src/utils/communicationDownload';

import { triggerAnchorDownload } from './downloadCommunicationUtils';

const LANGUAGE = getTypedLanguage(initLanguage);
const MESSAGES = flattenMessages(providerLocaleMap[LANGUAGE] as Record<string, string>);

const useStyles = makeStyles(() => ({
  container: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: 24,
    backgroundColor: Colors.CLASSICAL_WHITE,
  },
  title: {
    color: Colors.CHARCOAL_GREY,
    marginTop: 24,
    marginBottom: 8,
    textAlign: 'center',
  },
  hint: {
    color: Colors.SLATE_GREY,
    maxWidth: 420,
    textAlign: 'center',
  },
}));

interface CommunicationAttachmentsDownloadContentProps {
  communicationId: string;
}

const CommunicationAttachmentsDownloadContent = ({ communicationId }: CommunicationAttachmentsDownloadContentProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  useEffect(() => {
    triggerAnchorDownload(getCommunicationAttachmentsDownloadUrl(communicationId));
  }, [communicationId]);

  return (
    <Grid className={classes.container}>
      <LoadingProgress loading />
      <Typography variant="h6" className={classes.title}>
        {formatMessage({ id: 'communications.attachmentsDownload.preparing' })}
      </Typography>
      <Typography variant="body2" className={classes.hint}>
        {formatMessage({ id: 'communications.attachmentsDownload.hint' })}
      </Typography>
    </Grid>
  );
};

interface CommunicationAttachmentsDownloadPageProps {
  communicationId: string;
}

const CommunicationAttachmentsDownloadPage = ({ communicationId }: CommunicationAttachmentsDownloadPageProps) => (
  <IntlProvider locale={LANGUAGE.toLowerCase()} messages={MESSAGES}>
    <CommunicationAttachmentsDownloadContent communicationId={communicationId} />
  </IntlProvider>
);

export default CommunicationAttachmentsDownloadPage;
